import { createSequence } from '@olivierpascal/helpers';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IParsedOrganizationUserRoles } from '~/helpers/parseOrganizationUserRoles';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import {
  OrganizationUserListItem,
  OrganizationUserListItemSkeleton,
} from '~/components/molecules/OrganizationUserListItem';
import { Table } from '~/components/molecules/Table';
import { useOrganizationUsersQuery } from '~/lib/api/modules/Organizations/hooks/useOrganizationUsersQuery';

const GRID_TEMPLATE_COLUMNS = 'repeat(2, 1fr) minmax(140px, min-content)';
const DEFAULT_SKELETON_COUNT = 3;

export type IOrganizationUserListProps = IContainerProps & {
  organizationId: string;
  roles?: IParsedOrganizationUserRoles;
};

export const OrganizationUserList: React.FC<IOrganizationUserListProps> = (
  props,
) => {
  const { sx, organizationId, roles } = props;
  const { t } = useTranslation();
  const organizationUsersQuery = useOrganizationUsersQuery({
    organizationId,
  });

  const organizationUsers = organizationUsersQuery.data?.items;
  const isEmpty =
    !organizationUsersQuery.isLoading && !organizationUsers?.length;

  return (
    <ContentBlock sx={sx}>
      {organizationUsersQuery.isError ? (
        <ContentBlock.Body first>
          <ErrorMessage error={organizationUsersQuery.error} />
        </ContentBlock.Body>
      ) : isEmpty ? (
        <ContentBlock.Empty title={t('OrganizationUserList.empty')} />
      ) : (
        <Table gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
          <Table.Header>{t('common.name')}</Table.Header>
          <Table.Header>{t('common.role')}</Table.Header>
          <Table.Header />
          {organizationUsersQuery.isLoading
            ? createSequence(DEFAULT_SKELETON_COUNT).map((index) => (
                <OrganizationUserListItemSkeleton key={index} />
              ))
            : organizationUsers?.map((user) => (
                <OrganizationUserListItem
                  key={user._id}
                  organizationId={organizationId}
                  user={user}
                  roles={roles}
                />
              ))}
        </Table>
      )}
    </ContentBlock>
  );
};
